import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export interface IDEState {
  // Layout
  layout: any;
  setLayout: (layout: any) => void;
  
  // Theme
  theme: 'dark' | 'light';
  toggleTheme: () => void;
  
  // Loading states
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  
  // Active file
  activeFile: string | null;
  setActiveFile: (file: string | null) => void;
  
  // Open files
  openFiles: Array<{
    path: string;
    name: string;
    content: string;
    modified: boolean;
    language: string;
  }>;
  openFile: (file: { path: string; name: string; content: string; language: string }) => void;
  closeFile: (path: string) => void;
  updateFileContent: (path: string, content: string) => void;
  
  // Console logs
  consoleLogs: Array<{
    id: string;
    type: 'log' | 'warn' | 'error' | 'info';
    message: string;
    timestamp: Date;
  }>;
  addConsoleLog: (log: { type: 'log' | 'warn' | 'error' | 'info'; message: string }) => void;
  clearConsole: () => void;
  
  // Build state
  buildStatus: 'idle' | 'building' | 'success' | 'error';
  setBuildStatus: (status: 'idle' | 'building' | 'success' | 'error') => void;
  lastBuildUrl: string | null;
  setLastBuildUrl: (url: string | null) => void;
  
  // Game objects (for hierarchy)
  gameObjects: Array<{
    id: string;
    name: string;
    type: string;
    children: string[];
    parent: string | null;
    components: Array<{
      type: string;
      properties: Record<string, any>;
    }>;
  }>;
  setGameObjects: (objects: any[]) => void;
  addGameObject: (object: any) => void;
  removeGameObject: (id: string) => void;
  updateGameObject: (id: string, updates: any) => void;
  
  // Selected object
  selectedObjectId: string | null;
  setSelectedObject: (id: string | null) => void;
  
  // Assets
  assets: Array<{
    id: string;
    name: string;
    type: 'script' | 'texture' | 'audio' | 'prefab' | 'material' | 'scene';
    path: string;
    size: number;
    lastModified: Date;
  }>;
  setAssets: (assets: any[]) => void;
  addAsset: (asset: any) => void;
  removeAsset: (id: string) => void;
  
  // AI Interaction
  aiPrompt: string | null;
  setAiPrompt: (prompt: string | null) => void;

  // Panels visibility
  panelVisibility: Record<string, boolean>;
  togglePanel: (panelId: string) => void;
  showPanel: (panelId: string) => void;
  hidePanel: (panelId: string) => void;
}

export const useIDEStore = create<IDEState>()(
  persist(
    immer((set, get) => ({
      // Layout
      layout: null,
      setLayout: (layout) => set((state) => {
        state.layout = layout;
      }),
      
      // Theme
      theme: 'dark',
      toggleTheme: () => set((state) => {
        state.theme = state.theme === 'dark' ? 'light' : 'dark';
      }),
      
      // Loading states
      isLoading: false,
      setLoading: (loading) => set((state) => {
        state.isLoading = loading;
      }),
      
      // Active file
      activeFile: null,
      setActiveFile: (file) => set((state) => {
        state.activeFile = file;
      }),
      
      // Open files
      openFiles: [],
      openFile: (file) => set((state) => {
        const existingIndex = state.openFiles.findIndex(f => f.path === file.path);
        if (existingIndex >= 0) {
          // File already open, just activate it
          state.activeFile = file.path;
        } else {
          // Add new file
          state.openFiles.push({
            ...file,
            modified: false,
          });
          state.activeFile = file.path;
        }
      }),
      closeFile: (path) => set((state) => {
        state.openFiles = state.openFiles.filter(f => f.path !== path);
        if (state.activeFile === path) {
          state.activeFile = state.openFiles.length > 0 ? state.openFiles[0].path : null;
        }
      }),
      updateFileContent: (path, content) => set((state) => {
        const file = state.openFiles.find(f => f.path === path);
        if (file) {
          file.content = content;
          file.modified = true;
        }
      }),
      
      // Console logs
      consoleLogs: [],
      addConsoleLog: (log) => set((state) => {
        state.consoleLogs.push({
          id: Date.now().toString(),
          timestamp: new Date(),
          ...log,
        });
        // Keep only last 1000 logs
        if (state.consoleLogs.length > 1000) {
          state.consoleLogs = state.consoleLogs.slice(-1000);
        }
      }),
      clearConsole: () => set((state) => {
        state.consoleLogs = [];
      }),
      
      // Build state
      buildStatus: 'idle',
      setBuildStatus: (status) => set((state) => {
        state.buildStatus = status;
      }),
      lastBuildUrl: null,
      setLastBuildUrl: (url) => set((state) => {
        state.lastBuildUrl = url;
      }),
      
      // Game objects
      gameObjects: [
        {
          id: 'main-camera',
          name: 'Main Camera',
          type: 'Camera',
          children: [],
          parent: null,
          components: [
            {
              type: 'Camera',
              properties: {
                clearFlags: 'Skybox',
                cullingMask: 'Everything',
                projection: 'Perspective',
              }
            },
            {
              type: 'AudioListener',
              properties: {}
            }
          ]
        },
        {
          id: 'directional-light',
          name: 'Directional Light',
          type: 'Light',
          children: [],
          parent: null,
          components: [
            {
              type: 'Light',
              properties: {
                type: 'Directional',
                color: '#FFFFFF',
                intensity: 1,
              }
            }
          ]
        }
      ],
      setGameObjects: (objects) => set((state) => {
        state.gameObjects = objects;
      }),
      addGameObject: (object) => set((state) => {
        state.gameObjects.push({
          id: Date.now().toString(),
          children: [],
          parent: null,
          components: [],
          ...object,
        });
      }),
      removeGameObject: (id) => set((state) => {
        state.gameObjects = state.gameObjects.filter(obj => obj.id !== id);
        // Remove from children arrays
        state.gameObjects.forEach(obj => {
          obj.children = obj.children.filter(childId => childId !== id);
        });
        // Clear selection if removed object was selected
        if (state.selectedObjectId === id) {
          state.selectedObjectId = null;
        }
      }),
      updateGameObject: (id, updates) => set((state) => {
        const object = state.gameObjects.find(obj => obj.id === id);
        if (object) {
          Object.assign(object, updates);
        }
      }),
      
      // Selected object
      selectedObjectId: null,
      setSelectedObject: (id) => set((state) => {
        state.selectedObjectId = id;
      }),
      
      // Assets
      assets: [
        {
          id: 'player-controller',
          name: 'PlayerController.cs',
          type: 'script',
          path: 'Assets/Scripts/PlayerController.cs',
          size: 2048,
          lastModified: new Date(),
        },
        {
          id: 'game-manager',
          name: 'GameManager.cs',
          type: 'script',
          path: 'Assets/Scripts/GameManager.cs',
          size: 1536,
          lastModified: new Date(),
        },
        {
          id: 'player-sprite',
          name: 'player_idle.png',
          type: 'texture',
          path: 'Assets/Sprites/player_idle.png',
          size: 4096,
          lastModified: new Date(),
        }
      ],
      setAssets: (assets) => set((state) => {
        state.assets = assets;
      }),
      addAsset: (asset) => set((state) => {
        state.assets.push({
          id: Date.now().toString(),
          lastModified: new Date(),
          ...asset,
        });
      }),
      removeAsset: (id) => set((state) => {
        state.assets = state.assets.filter(asset => asset.id !== id);
      }),
      
      // AI Interaction
      aiPrompt: null,
      setAiPrompt: (prompt) => set((state) => {
        state.aiPrompt = prompt;
      }),

      // Panels visibility
      panelVisibility: {
        hierarchy: true,
        scene: true,
        'ai-chat': true,
        'code-editor': true,
        inspector: true,
        assets: true,
        console: true,
        build: true,
      },
      togglePanel: (panelId) => set((state) => {
        state.panelVisibility[panelId] = !state.panelVisibility[panelId];
      }),
      showPanel: (panelId) => set((state) => {
        state.panelVisibility[panelId] = true;
      }),
      hidePanel: (panelId) => set((state) => {
        state.panelVisibility[panelId] = false;
      }),
    })),
    {
      name: 'stan-ide-store',
      partialize: (state) => ({
        layout: state.layout,
        theme: state.theme,
        panelVisibility: state.panelVisibility,
      }),
    }
  )
);